import type { AnnotationStatus } from "@shared/entities";
import type { Element, GearRarity, StageEvent } from "@shared/game";
import { ELEMENT_LABEL, RARITY_LABEL, STAGE_EVENT_LABEL } from "@shared/game";

/*
 * Colour means data. These are the only coloured chips in the app: an element,
 * a rarity, a stage event, a status. `var(--color-<key>)` resolves to the
 * tokens in styles.css, so a palette change is one edit.
 */

export function ElementChip({ element, dim = false }: { element: Element; dim?: boolean }) {
	return (
		<span
			className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
			style={{
				color: `var(--color-${element})`,
				background: `color-mix(in oklab, var(--color-${element}) ${dim ? 8 : 14}%, transparent)`,
			}}
		>
			<span className="size-1.5 rounded-full" style={{ background: `var(--color-${element})` }} />
			{ELEMENT_LABEL[element]}
		</span>
	);
}

export function RarityText({ rarity, children }: { rarity: GearRarity | string; children?: React.ReactNode }) {
	return (
		<span style={{ color: `var(--color-${rarity})` }} className="font-medium">
			{children ?? RARITY_LABEL[rarity as GearRarity] ?? rarity}
		</span>
	);
}

const EVENT_STYLE: Record<StageEvent, { bg: string; fg: string }> = {
	battle: { bg: "var(--color-ink-700)", fg: "var(--color-ink-300)" },
	skill_selection: { bg: "var(--color-water)", fg: "var(--color-ink-950)" },
	boon_selection: { bg: "var(--color-wind)", fg: "var(--color-ink-950)" },
	mini_boss: { bg: "var(--color-legendary)", fg: "var(--color-ink-950)" },
	boss: { bg: "var(--color-mythic)", fg: "var(--color-ink-950)" },
};

export function EventChip({ event }: { event: StageEvent }) {
	const s = EVENT_STYLE[event];
	return (
		<span className="inline-flex rounded px-1.5 py-0.5 text-[0.7rem] font-semibold" style={{ background: s.bg, color: s.fg }}>
			{STAGE_EVENT_LABEL[event]}
		</span>
	);
}
export const eventStyle = (event: StageEvent) => EVENT_STYLE[event];

const STATUS_LABEL: Record<AnnotationStatus, string> = { draft: "Draft", in_review: "In review", final: "Final" };
const STATUS_COLOR: Record<AnnotationStatus, string> = {
	draft: "var(--color-ink-400)",
	in_review: "var(--color-warn)",
	final: "var(--color-ok)",
};

export function StatusDot({ status, label = true }: { status: AnnotationStatus; label?: boolean }) {
	return (
		<span className="inline-flex items-center gap-1.5 text-xs" style={{ color: STATUS_COLOR[status] }} title={STATUS_LABEL[status]}>
			<span className="size-1.5 rounded-full" style={{ background: STATUS_COLOR[status] }} />
			{label && STATUS_LABEL[status]}
		</span>
	);
}

export function Implemented({ value }: { value: boolean }) {
	return (
		<span
			className="text-xs"
			style={{ color: value ? "var(--color-ok)" : "var(--color-ink-400)" }}
			title={value ? "The game has a handler for this effect" : "Not offered in the game yet: no handler"}
		>
			{value ? "In game" : "Not yet"}
		</span>
	);
}
